export type RecordStatus = "new" | "read" | "archived";
export type AdminView = "inquiries" | "inbound" | "outbound" | "users";

export type Metrics = {
  contacts: number;
  unreadContacts: number;
  emails: number;
  unreadEmails: number;
  sent: number;
  failed: number;
  users: number;
};

export type Pagination = {
  page: number;
  pageSize: number;
  total: number;
  pages: number;
};

export type InquiryRecord = {
  id: string;
  name: string;
  email: string;
  organization: string;
  topics: string[];
  message: string;
  locale: "zh" | "en";
  status: RecordStatus;
  createdAt: string;
  updatedAt: string;
};

export type InboundRecord = {
  id: string;
  from: string;
  to: string;
  subject: string;
  size: number;
  status: RecordStatus;
  bodyPreview: string;
  hasAttachments: boolean;
  createdAt: string;
  updatedAt: string;
};

export type InboundDetail = InboundRecord & {
  text: string;
  replyTo: string;
  messageId: string | null;
  attachments: Array<{
    index: number;
    filename: string;
    contentType: string;
    size: number;
    previewable: boolean;
  }>;
};

export type OutboundRecord = {
  id: string;
  kind: "new" | "reply" | "forward" | "notification";
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  status: "queued" | "sending" | "sent" | "failed";
  attemptCount: number;
  messageId: string | null;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
  sentAt: string | null;
};

export type OutboundDetail = OutboundRecord & {
  textBody: string;
  events: Array<{
    id: string;
    status: string;
    message: string;
    createdAt: string;
  }>;
  attachments: Array<{
    id: string;
    filename: string;
    contentType: string;
    size: number;
  }>;
};

export type ViewRecords<T> = {
  view: AdminView;
  records: T[];
};

export type AdminUser = {
  id: string;
  name: string;
  email: string;
  role: string;
  banned: boolean;
  banReason: string | null;
  createdAt: string;
};

export type ListResponse<T> = {
  records: T[];
  pagination: Pagination;
};

export const emptyMetrics: Metrics = {
  contacts: 0,
  unreadContacts: 0,
  emails: 0,
  unreadEmails: 0,
  sent: 0,
  failed: 0,
  users: 0,
};

export const emptyPagination: Pagination = {
  page: 1,
  pageSize: 20,
  total: 0,
  pages: 1,
};

export async function api<T>(input: string, init?: RequestInit): Promise<T> {
  const response = await fetch(input, { credentials: "include", ...init });
  const body = (await response.json().catch(() => null)) as
    | (T & { error?: string })
    | null;
  if (!response.ok) {
    throw new Error(body?.error || `Request failed (${response.status})`);
  }
  return body as T;
}

export function listUrl(
  view: Exclude<AdminView, "users">,
  page: number,
  query: string,
  status: string,
) {
  const path =
    view === "inquiries"
      ? "/api/admin/inquiries"
      : view === "inbound"
        ? "/api/admin/emails/inbound"
        : "/api/admin/emails/outbound";
  const params = new URLSearchParams({ page: String(page), pageSize: "20" });
  if (query.trim()) params.set("q", query.trim());
  if (status && status !== "all") params.set("status", status);
  return `${path}?${params}`;
}

export function formatBytes(value: number) {
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / 1024 / 1024).toFixed(1)} MB`;
}

export function recordsForView<T>(view: AdminView, recordSet: ViewRecords<T>) {
  return recordSet.view === view ? recordSet.records : [];
}

export function formatRecipients(value: unknown) {
  const recipients = Array.isArray(value)
    ? value
    : typeof value === "string"
      ? [value]
      : [];
  return recipients
    .filter((recipient): recipient is string => typeof recipient === "string")
    .join(", ");
}

export function adminInviteEmail(token: string) {
  if (!token || token.length > 4096) return "";
  const encodedPayload = token.split(".")[0];
  if (!encodedPayload || !/^[A-Za-z0-9_-]+$/.test(encodedPayload)) return "";
  try {
    const padding = "=".repeat((4 - (encodedPayload.length % 4)) % 4);
    const binary = atob(
      encodedPayload.replace(/-/g, "+").replace(/_/g, "/") + padding,
    );
    const payload = JSON.parse(
      new TextDecoder().decode(
        Uint8Array.from(binary, (character) => character.charCodeAt(0)),
      ),
    ) as { email?: unknown };
    return typeof payload.email === "string" ? payload.email : "";
  } catch {
    return "";
  }
}
